/**
 * PROG-WEEK-PARAMS/FE-5 — extracted from ExerciseProgressionEditor.tsx.
 *
 * Pure presentational per-week wrapper. Owns the outer week `<Card>` chrome
 * (title, set count, remove button), the W1-W10 week-level field row, and
 * the list of `ExerciseProgressionSetRow` children + the "Aggiungi Set"
 * button. Parent (ExerciseProgressionEditor) still owns all state.
 *
 * `data-testid` values are preserved verbatim.
 */
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripVertical,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useCallback, useId, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import type {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
} from "../../types/Entities";
import { simulateChain } from "../../utils/precompiledChain";
import { computeSetDivergence } from "../../utils/progressionDivergence";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";
import { FieldGroup, ModeToggle } from "./ExerciseProgressionEditor.helpers";
import ExerciseProgressionSetRow from "./ExerciseProgressionSetRow";
import type { ExerciseProgressionEditorMode } from "./ExerciseProgressionEditor";
import WeekDateControl from "./WeekDateControl";

export interface ExerciseProgressionWeekRowProps {
  wi: number;
  week: PrecompiledWeek;
  weeks: PrecompiledWeek[];
  /** PROG-DND/DND-1 — drag handle for week reordering. When omitted (e.g.
   *  on a structureLocked mount that skips the DnD wrapper) no grip icon
   *  renders. */
  dragHandleProps?: import("react-beautiful-dnd").DraggableProvidedDragHandleProps | null;
  /** Raw (un-normalized) source weeks; SetRow needs the matching raw set
   *  for "Eredita" chip detection. */
  rawWeeks: PrecompiledWeek[] | undefined;
  hypLoadGrid: (number | null)[][];
  /** PROG-BUGS/ENGINE-3 (Bug A) — reps projection grid parallel to hypLoadGrid. */
  hypRepsGrid: (number | string | null)[][];
  structureLocked: boolean;
  workDataLocked: boolean;
  mediaLocked: boolean;
  mode: ExerciseProgressionEditorMode;
  isCompact: boolean;
  options?: ProgEditorOptions;
  weekRepSchemeOpts: { label: string; value: string }[];
  weekEffortOpts: { label: string; value: string }[];
  setRepSchemeOptsByKey: Record<string, { label: string; value: string }[]>;
  setEffortOptsByKey: Record<string, { label: string; value: string }[]>;
  lookupTecnica: (
    val: string | number | null | undefined,
  ) => { includeXReps?: boolean; dropXReps?: boolean } | undefined;
  onRemoveWeek: () => void;
  /** Whole-week copy / paste (reuses shared `copiedPrecompiledWeek` slot). */
  onCopyWeek: () => void;
  onPasteWeek: () => void;
  /** True when the shared week clipboard slot is populated. */
  canPasteWeek: boolean;
  /** Whole-set copy / paste, forwarded per-set to ExerciseProgressionSetRow. */
  onCopySet: (si: number) => void;
  onPasteSet: (si: number) => void;
  /** True when the shared set clipboard slot is populated. */
  canPasteSet: boolean;
  onPatchWeek: (patch: Partial<PrecompiledWeek>) => void;
  onUpdateWeekField: <K extends keyof PrecompiledWeek>(
    field: K,
    val: PrecompiledWeek[K],
  ) => void;
  onAddSet: () => void;
  onRemoveSet: (si: number) => void;
  onPatchSet: (si: number, patch: Partial<PrecompiledSet>) => void;
  onPatchSetStructure: (si: number, patch: Partial<PrecompiledSet>) => void;
  onUpdateSetRule: (
    si: number,
    partial: Partial<PrecompiledWeekProgressionRule>,
  ) => void;
  /** PROG-DND/DND-2 — parent-owned reorder. Called with (from, to) source
   *  / destination indices within this week's `sets` array. No-op
   *  contract: the parent already gates on `structureLocked`. */
  onReorderSets: (from: number, to: number) => void;
}

export default function ExerciseProgressionWeekRow({
  wi,
  week,
  weeks,
  dragHandleProps,
  rawWeeks,
  hypLoadGrid,
  hypRepsGrid,
  structureLocked,
  workDataLocked,
  mediaLocked,
  mode,
  isCompact,
  options,
  weekRepSchemeOpts,
  weekEffortOpts,
  setRepSchemeOptsByKey,
  setEffortOptsByKey,
  lookupTecnica,
  onRemoveWeek,
  onCopyWeek,
  onPasteWeek,
  canPasteWeek,
  onCopySet,
  onPasteSet,
  canPasteSet,
  onPatchWeek,
  onUpdateWeekField,
  onAddSet,
  onRemoveSet,
  onPatchSet,
  onPatchSetStructure,
  onUpdateSetRule,
  onReorderSets,
}: ExerciseProgressionWeekRowProps) {
  // PROG-DND/DND-2 — per-week-instance droppable id so two weeks on the
  // same page have distinct droppable namespaces (also distinct from the
  // outer weeks Droppable from DND-1).
  const setsDndId = useId();
  const setsDroppableId = `editor-sets-${wi}-${setsDndId}`;
  // Weeks start COLLAPSED by default in the read-only views (trainer-view /
  // athlete-monitor) so an exercise shows a compact list of weeks; while
  // editing (trainer-edit) they stay open so the trainer can build them.
  const [weekCollapsed, setWeekCollapsed] = useState(mode !== "trainer-edit");
  const handleSetDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      onReorderSets(result.source.index, result.destination.index);
    },
    [onReorderSets],
  );
  return (
    <Card
      size="small"
      data-testid={`progression-week-${wi}`}
      headStyle={{ flexWrap: "wrap", gap: 8 }}
      title={
        <Space>
          <span
            onClick={() => setWeekCollapsed((v) => !v)}
            role="button"
            aria-label={weekCollapsed ? "Espandi settimana" : "Comprimi settimana"}
            aria-expanded={!weekCollapsed}
            style={{
              cursor: "pointer",
              color: "var(--app-gray-500)",
              padding: "0 4px",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={weekCollapsed ? faChevronRight : faChevronDown}
            />
          </span>
          {dragHandleProps && (
            <span
              {...dragHandleProps}
              style={{
                cursor: "grab",
                color: "var(--app-gray-400)",
                fontSize: 12,
                padding: "0 4px",
                display: "inline-flex",
                alignItems: "center",
                transition: "color 0.15s ease",
              }}
              title={`Trascina per riordinare settimana ${week.weekNumber}`}
              aria-label={`Trascina per riordinare settimana ${week.weekNumber}`}
              data-testid={`progression-week-drag-handle-${wi}`}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--app-gray-600)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--app-gray-400)")}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          )}
          <Typography.Text strong>Settimana {week.weekNumber}</Typography.Text>
          <Tag>{week.sets?.length ?? 0} set</Tag>
          {(() => {
            // PROG-ENGINE-V2/5 (#d55fc4ee) — aggregate divergence badge.
            // Counts sets in this week whose executed values diverge from
            // the engine's suggested grid values. Silent when zero.
            const sets = week.sets ?? [];
            const divergedCount = sets.reduce((acc, s, si) => {
              const d = computeSetDivergence(
                s,
                hypLoadGrid[wi]?.[si] ?? null,
                hypRepsGrid[wi]?.[si] ?? null,
              );
              return d ? acc + 1 : acc;
            }, 0);
            if (divergedCount === 0) return null;
            return (
              <Tag
                color="orange"
                data-testid={`progression-week-divergence-${wi}`}
                title="Set con esecuzione fuori dal suggerito (>5% kg o reps differenti)"
              >
                {divergedCount} set fuori suggerito
              </Tag>
            );
          })()}
        </Space>
      }
      extra={
        // stopPropagation so interacting with the date picker / calendar menu
        // doesn't toggle the week panel.
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <WeekDateControl
            weekNumber={week.weekNumber}
            date={week.date}
            onChange={
              structureLocked ? undefined : (iso) => onPatchWeek({ date: iso })
            }
          />
          {!structureLocked && (
            <>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={onCopyWeek}
                data-testid={`progression-week-copy-${wi}`}
                aria-label={`Copia settimana ${week.weekNumber}`}
              >
                Copia settimana
              </Button>
              <Button
                type="text"
                size="small"
                icon={<SnippetsOutlined />}
                onClick={onPasteWeek}
                disabled={!canPasteWeek}
                data-testid={`progression-week-paste-${wi}`}
                aria-label={`Incolla settimana ${week.weekNumber}`}
              >
                Incolla settimana
              </Button>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={onRemoveWeek}
                data-testid={`progression-week-remove-${wi}`}
                aria-label={`Rimuovi settimana ${week.weekNumber}`}
              />
            </>
          )}
        </Space>
      }
    >
      {!weekCollapsed && (
      <>
      {/* PROG-WEEK-PARAMS/FE-1 — week-level W1-W8 row. */}
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <FieldGroup label="Rep Scheme Type">
            <Select
              style={{ width: "100%" }}
              options={options?.repSchemeTypeOptions ?? []}
              value={week.repSchemeTypeId || undefined}
              disabled={structureLocked}
              onChange={(val) =>
                onPatchWeek({
                  repSchemeTypeId: (val as number) ?? 0,
                  repScheme: "",
                })
              }
              placeholder="Seleziona tipo..."
              allowClear
              data-testid={`progression-week-repschemetype-${wi}`}
            />
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Rep Scheme">
            {options ? (
              <AutoComplete
                style={{ width: "100%" }}
                options={weekRepSchemeOpts}
                value={week.repScheme || undefined}
                disabled={structureLocked || !week.repSchemeTypeId}
                onChange={(val) =>
                  onUpdateWeekField("repScheme", (val as string) ?? "")
                }
                placeholder={
                  week.repSchemeTypeId
                    ? "Seleziona o digita (es. 8-12)"
                    : "Prima seleziona il tipo"
                }
                allowClear
                filterOption={(input, opt) =>
                  String(opt?.label ?? opt?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                data-testid={`progression-week-repscheme-${wi}`}
              />
            ) : (
              <Input
                value={week.repScheme ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onUpdateWeekField("repScheme", e.target.value)
                }
                placeholder="es. 5"
                data-testid={`progression-week-repscheme-${wi}`}
              />
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Effort Type">
            <Select
              style={{ width: "100%" }}
              options={options?.effortTypeOptions ?? []}
              value={week.effortTypeId || undefined}
              disabled={structureLocked}
              onChange={(val) =>
                onPatchWeek({
                  effortTypeId: (val as number) ?? 0,
                  effort: "",
                })
              }
              placeholder="Seleziona tipo..."
              allowClear
              data-testid={`progression-week-efforttype-${wi}`}
            />
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Effort">
            <Select
              style={{ width: "100%" }}
              options={weekEffortOpts}
              value={week.effort || undefined}
              disabled={structureLocked || !week.effortTypeId}
              onChange={(val) =>
                onUpdateWeekField("effort", (val as string) ?? "")
              }
              placeholder={
                week.effortTypeId
                  ? "Seleziona effort..."
                  : "Prima seleziona il tipo"
              }
              allowClear
              data-testid={`progression-week-effort-${wi}`}
            />
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Time Under Tension">
            <ModeToggle
              value={(week.tutMode ?? "select") as "select" | "free"}
              disabled={structureLocked}
              onChange={(v) => onPatchWeek({ tutMode: v, tut: null })}
              options={[
                { label: "Seleziona", value: "select" },
                { label: "Libero", value: "free" },
              ]}
              testId={`progression-week-tutmode-${wi}`}
            />
            {(week.tutMode ?? "select") === "select" ? (
              <Select
                style={{ width: "100%" }}
                options={options?.tutOptions ?? []}
                value={
                  week.tut != null && week.tut !== ""
                    ? Number(week.tut)
                    : undefined
                }
                disabled={structureLocked}
                onChange={(val) =>
                  onUpdateWeekField("tut", val != null ? String(val) : null)
                }
                placeholder="Seleziona TUT..."
                allowClear
                data-testid={`progression-week-tut-${wi}`}
              />
            ) : (
              <Input
                value={week.tut ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onUpdateWeekField("tut", e.target.value || null)
                }
                placeholder="es. 3-1-2-0"
                data-testid={`progression-week-tut-${wi}`}
              />
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Tecnica">
            <ModeToggle
              value={(week.tecnicaMode ?? "select") as "select" | "free"}
              disabled={structureLocked}
              onChange={(v) =>
                onPatchWeek({
                  tecnicaMode: v,
                  tecnica: null,
                  xRepsTarget: null,
                  xRepsLoadDropPerc: null,
                })
              }
              options={[
                { label: "Seleziona", value: "select" },
                { label: "Libero", value: "free" },
              ]}
              testId={`progression-week-tecnicamode-${wi}`}
            />
            {(week.tecnicaMode ?? "select") === "select" ? (
              <Select
                style={{ width: "100%" }}
                options={options?.tecnicaOptions ?? []}
                value={
                  week.tecnica != null && week.tecnica !== ""
                    ? Number(week.tecnica)
                    : undefined
                }
                disabled={structureLocked}
                onChange={(val) =>
                  onPatchWeek({
                    tecnica: val != null ? String(val) : null,
                    xRepsTarget: null,
                    xRepsLoadDropPerc: null,
                  })
                }
                placeholder="Seleziona tecnica..."
                allowClear
                data-testid={`progression-week-tecnica-${wi}`}
              />
            ) : (
              <Input
                value={week.tecnica ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onUpdateWeekField("tecnica", e.target.value || null)
                }
                placeholder="es. Drop Set"
                data-testid={`progression-week-tecnica-${wi}`}
              />
            )}
          </FieldGroup>
        </Col>
      </Row>

      <div style={{ marginTop: 12 }}>
        {(() => {
          const setsArr = week.sets ?? [];
          const renderSetRow = (
            set: PrecompiledSet,
            si: number,
            dragHandleProps?:
              | import("react-beautiful-dnd").DraggableProvidedDragHandleProps
              | null,
          ) => {
            const hypLoad = hypLoadGrid[wi]?.[si] ?? null;
            const hypReps = hypRepsGrid[wi]?.[si] ?? null;
            const chain = simulateChain(weeks, wi, si);
            const rawSet = rawWeeks?.[wi]?.sets?.[si];
            const key = `${wi}-${si}`;
            return (
              <ExerciseProgressionSetRow
                wi={wi}
                si={si}
                week={week}
                weeks={weeks}
                set={set}
                rawSet={rawSet}
                hypLoad={hypLoad}
                hypReps={hypReps}
                chain={chain}
                structureLocked={structureLocked}
                workDataLocked={workDataLocked}
                mediaLocked={mediaLocked}
                mode={mode}
                isCompact={isCompact}
                options={options}
                setRepSchemeOpts={setRepSchemeOptsByKey[key] ?? []}
                setEffortOpts={setEffortOptsByKey[key] ?? []}
                lookupTecnica={lookupTecnica}
                onPatchSet={(patch) => onPatchSet(si, patch)}
                onPatchSetStructure={(patch) =>
                  onPatchSetStructure(si, patch)
                }
                onUpdateSetRule={(partial) => onUpdateSetRule(si, partial)}
                onRemoveSet={() => onRemoveSet(si)}
                onCopySet={() => onCopySet(si)}
                onPasteSet={() => onPasteSet(si)}
                canPasteSet={canPasteSet}
                prevWeekHypLoad={
                  wi > 0 ? hypLoadGrid[wi - 1]?.[si] ?? null : null
                }
                prevWeekSetRule={
                  wi > 0
                    ? weeks[wi - 1].sets?.[si]?.progressionRule ?? null
                    : null
                }
                prevSetHypLoad={
                  si > 0 ? hypLoadGrid[wi]?.[si - 1] ?? null : null
                }
                prevSetRule={
                  si > 0
                    ? week.sets?.[si - 1]?.progressionRule ?? null
                    : null
                }
                prevWeekSet={
                  wi > 0 ? weeks[wi - 1].sets?.[si] ?? null : null
                }
                prevSetInWeek={
                  si > 0 ? week.sets?.[si - 1] ?? null : null
                }
                dragHandleProps={dragHandleProps}
              />
            );
          };

          return (
            <>
              {structureLocked ? (
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  {setsArr.map((set, si) => (
                    <div key={si}>{renderSetRow(set, si)}</div>
                  ))}
                </Space>
              ) : (
                <DragDropContext onDragEnd={handleSetDragEnd}>
                  <StrictModeDroppable droppableId={setsDroppableId}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          width: "100%",
                        }}
                      >
                        {setsArr.map((set, si) => (
                          <Draggable
                            key={si}
                            draggableId={`${setsDroppableId}-${si}`}
                            index={si}
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                data-testid={`progression-set-draggable-${wi}-${si}`}
                              >
                                {renderSetRow(
                                  set,
                                  si,
                                  dragProvided.dragHandleProps,
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>
                </DragDropContext>
              )}

              {!structureLocked && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={onAddSet}
                  block
                  data-testid={`progression-set-add-${wi}`}
                  style={{ marginTop: 8 }}
                >
                  Aggiungi Set
                </Button>
              )}
            </>
          );
        })()}
      </div>
      </>
      )}
    </Card>
  );
}
